import {
  DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined
} from '@ant-design/icons';
import {
  Button,
  message,
  Modal,
  Space,
  Switch,
  Tooltip,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps, FilterValueProps, mappingFilterFields,
  mappingFilterToQuery, mappingParamsFilter, mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import useDidMount from 'common/hooks/useDidMount';
import { changeStatusClientService, deleteClientService, getClientListService } from 'common/services/extends/client';
import {
  ROUTE_PATHS
} from 'common/utils/constant';
import { formatDateTime } from 'common/utils/functions';

export interface ClientData {
  id: number,
  code: string,
  name: string,
  status: number,
  startDate: string,
  endDate: string,
  createdAt: string,
  updatedAt: string,
}

const ClientManagement: React.FC<ActiveRoles> = ({ roleCreate, roleUpdate, roleDelete }) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigation = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);

  const pageParam = searchParams.get('page');

  /* Selectors */
  const { system: { defaultPageSize, advancedFilter } } = useAppSelector((state) => state);

  /* State */
  const [currentPage, setCurrentPage] = useState(Number(pageParam));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));

  /* Variables */
  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('client', advancedFilter),
    [advancedFilter]
  );

  const queryKey = ['getClientListService', keyword, currentPage, currentView, selectedFilterList];

  /* Queries */
  const { data: clientData, isLoading } = useQuery(
    queryKey,
    () => getClientListService({
      keyword,
      page: currentPage,
      limit: currentView,
      ...returnParamsFilter,
    }),
    {
      enabled: !!currentPage,
    }
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['deleteClientMutate'],
    async (ids: number[]) => deleteClientService(ids),
    {
      onSuccess: () => {
        message.success(t('message.deleteSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            message.error(error?.message || '');
          });
        } else {
          message.error(t('message.deleteError'));
        }
      },
    }
  );

  const { mutate: updateStatusMutate, isLoading: updateStatusLoading } = useMutation(
    'updateClientStatus',
    async (data: {
      id: number, statusId: number
    }) => changeStatusClientService(data.id, data.statusId),
    {
      onSuccess: () => {
        message.success(t('message.updateStatusSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.updateStatusError'));
      }
    }
  );

  const tableData: ClientData[] = useMemo(() => (
    clientData?.data.map((val) => ({
      id: val.clientData.id,
      code: val.clientData.code || '',
      name: val.clientData?.name || '',
      status: val.clientData?.activityStatus,
      createdAt: val.clientData?.createdAt || '',
      updatedAt: val.clientData?.updatedAt || '',
      startDate: val.clientData?.startDate || '',
      endDate: val.clientData?.endDate || '',
    })) || []), [clientData]);

  const columns: ColumnsType<ClientData> = [
    {
      title: t('system.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_name: string, data: ClientData) => (
        <Typography.Text>
          {formatDateTime(data.createdAt || '')}
        </Typography.Text>
      ),
    },
    {
      title: t('system.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_name: string, data: ClientData) => (
        <Typography.Text>
          {formatDateTime(data.updatedAt || '')}
        </Typography.Text>
      ),
    },
    {
      title: 'Client ID',
      key: 'code',
      render: (_name: string, data: ClientData) => (
        <Typography.Text>
          {data.code || ''}
        </Typography.Text>
      ),
    },
    {
      title: 'Company',
      key: 'name',
      render: (_name: string, data: ClientData) => (
        <Typography.Text>
          {data.name || ''}
        </Typography.Text>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_name: string, data: ClientData) => (
        <Switch
          checked={data.status === 2}
          onChange={(checked) => updateStatusMutate({
            id: data.id,
            statusId: checked ? 2 : 1,
          })}
        />
      ),
    },
    {
      title: t('system.startDate'),
      dataIndex: 'startDate',
      key: 'startDate',
      render: (_name: string, data: ClientData) => (
        <Typography.Text>
          {formatDateTime(data.startDate || '')}
        </Typography.Text>
      ),
    },
    {
      title: t('system.endDate'),
      dataIndex: 'endDate',
      key: 'endDate',
      render: (_name: string, data: ClientData) => (
        <Typography.Text>
          {formatDateTime(data.endDate || '')}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      render: (_name: string, data: ClientData) => (
        <Space>
          <Tooltip title="View">
            <Button
              icon={<EyeOutlined />}
              onClick={() => navigation(`${ROUTE_PATHS.CLIENT_DETAIL}?id=${data.id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => navigation(`${ROUTE_PATHS.CLIENT_EDIT}?id=${data.id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              disabled={!roleDelete || deleteLoading}
              loading={deleteLoading}
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  className: 't-pagetable_deleteRecordModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmDeleteRecord'),
                  onOk: () => {
                    deleteMutate([data.id]);
                  },
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Change page view
  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: page.toString() }, { replace: true });
  };

  const handleDeleteMultiple = (data: ClientData[]) => {
    const ids = data.map((item) => item.id);
    if (ids.length > 0) {
      deleteMutate(ids);
    }
  };

  // Change page size
  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
  };

  // Add filter
  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
  };

  /* Effects */
  useEffect(() => {
    setCurrentPage(pageParam ? Number(pageParam) : 1);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList),
      page: pageParam || '1'
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, setSearchParams, selectedFilterList]);

  useDidMount(() => {
    if (pageParam) return setCurrentPage(Number(pageParam));
    return setCurrentPage(Number(pageParam) || 1);
  });

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.client')}
        rightHeader={(
          <Space>
            <Button
              disabled={!roleCreate}
              type="primary"
              onClick={() => navigation(ROUTE_PATHS.CLIENT_EDIT)}
            >
              <PlusOutlined />
              {t('system.create')}
            </Button>
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          handleDelete={handleDeleteMultiple}
          isLoading={isLoading || deleteLoading || updateStatusLoading}
          handleSearch={setKeyword}
          tableProps={{
            initShowColumns: ['createdAt', 'updatedAt', 'code', 'name', 'status', 'startDate', 'endDate', 'action'],
            columns,
            pageData: tableData || [],
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: clientData?.meta.total ?? 1,
            noBaseCol: true,
            noDeleteLanguage: true,
            filterFields,
          }}
          roles={{ roleCreate, roleUpdate, roleDelete }}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter
          }}
        />
      </div>
    </>
  );
};

export default ClientManagement;
